import { nuxtAuthRepository } from '~~/adapters/repositories/nuxt/nuxtAuthRepository';

import { useLeadFlowLogger } from './useLeadFlowLogger';

export interface OtpValidationResult {
	success: boolean;
	error?: string;
}

export interface PhoneRegistrationData {
	phoneNumber: string;
	otp: string;
	email?: string;
	name?: string;
}

export function useAuthService() {
	const logger = useLeadFlowLogger('AuthService');
	async function sendOtpCode(
		phoneNumber: string
	): Promise<OtpValidationResult> {
		logger.info('Enviando código OTP', { phoneNumber });
		try {
			const [_, error] = await nuxtAuthRepository.phone({ phoneNumber });

			if (error) {
				logger.warn('Falha ao enviar código OTP (API retornou erro)', {
					phoneNumber,
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode
				});
				return {
					success: false,
					error: error.message || 'Erro ao enviar código OTP'
				};
			}

			logger.info('Código OTP enviado com sucesso', { phoneNumber });
			return { success: true };
		} catch (error) {
			logger.error('Erro inesperado ao enviar código OTP', {
				phoneNumber,
				error
			});
			return {
				success: false,
				error: 'Erro inesperado ao enviar código OTP'
			};
		}
	}

	async function validatePhoneOtp(
		data: PhoneRegistrationData
	): Promise<OtpValidationResult> {
		logger.info('Validando código OTP de telefone', {
			phoneNumber: data.phoneNumber,
			hasEmail: !!data.email,
			hasName: !!data.name
		});
		try {
			const [_, error] = await nuxtAuthRepository.getCreatePhoneUser(data);

			if (error) {
				logger.warn('Falha ao validar OTP de telefone (API retornou erro)', {
					phoneNumber: data.phoneNumber,
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode
				});
				return {
					success: false,
					error: error.message || 'Erro ao validar código OTP'
				};
			}

			logger.info('OTP de telefone validado com sucesso', {
				phoneNumber: data.phoneNumber
			});
			return { success: true };
		} catch (error) {
			logger.error('Erro inesperado ao validar código OTP', {
				phoneNumber: data.phoneNumber,
				error
			});
			return {
				success: false,
				error: 'Erro inesperado ao validar código OTP'
			};
		}
	}

	async function validateSocialOtp(otp: string): Promise<OtpValidationResult> {
		logger.info('Validando código OTP social');
		try {
			const [_, error] = await nuxtAuthRepository.verifySocialRegistration({
				otp
			});

			if (error) {
				logger.warn('Falha ao validar OTP social (API retornou erro)', {
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode
				});
				return {
					success: false,
					error: error.message || 'Erro ao validar código OTP'
				};
			}

			logger.info('OTP social validado com sucesso');
			return { success: true };
		} catch (error) {
			logger.error('Erro inesperado ao validar código OTP social', {
				error
			});
			return {
				success: false,
				error: 'Erro inesperado ao validar código OTP'
			};
		}
	}

	async function completePhoneRegistration(
		name: string,
		email?: string
	): Promise<OtpValidationResult> {
		logger.info('Completando cadastro de usuário por telefone', {
			hasEmail: !!email
		});
		try {
			const [_, error] = await nuxtAuthRepository.completePhoneRegistration({
				name,
				...(email ? { email } : {})
			});

			if (error) {
				logger.warn('Falha ao completar cadastro por telefone', {
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode
				});
				return {
					success: false,
					error: error.message || 'Erro ao completar cadastro'
				};
			}

			logger.info('Cadastro por telefone completado com sucesso');
			return { success: true };
		} catch (error) {
			logger.error('Erro inesperado ao completar cadastro', {
				name,
				email,
				error
			});
			return {
				success: false,
				error: 'Erro inesperado ao completar cadastro'
			};
		}
	}

	async function completeSocialRegistration(
		phoneNumber: string
	): Promise<OtpValidationResult> {
		logger.info('Completando cadastro social', { phoneNumber });
		try {
			const [_, error] = await nuxtAuthRepository.completeSocialRegistration({
				phoneNumber
			});

			if (error) {
				logger.warn('Falha ao completar cadastro social', {
					phoneNumber,
					errorSource: error.source,
					errorMessage: error.message,
					errorStatusCode: (
						error.originalErrorObject as { statusCode?: number }
					)?.statusCode
				});
				return {
					success: false,
					error: error.message || 'Erro ao completar cadastro social'
				};
			}

			logger.info('Cadastro social completado com sucesso', { phoneNumber });
			return { success: true };
		} catch (error) {
			logger.error('Erro inesperado ao completar cadastro social', {
				phoneNumber,
				error
			});
			return {
				success: false,
				error: 'Erro inesperado ao completar cadastro social'
			};
		}
	}

	return {
		sendOtpCode,
		validatePhoneOtp,
		validateSocialOtp,
		completePhoneRegistration,
		completeSocialRegistration
	};
}
